import { PolicyType } from './policy';
export var ActionStatus;
(function (ActionStatus) {
    /** Ready to be processed, depending on action_on date */
    ActionStatus["PENDING"] = "pending";
    /** Processed */
    ActionStatus["COMPLETE"] = "complete";
    /** Failed */
    ActionStatus["FAILED"] = "failed";
    /** Like pending, but should not be automatically processed */
    ActionStatus["SUSPENDED"] = "suspended";
    /** When a late fee, or some other future action type? has been reverted */
    ActionStatus["REVERTED"] = "reverted";
    /** When the policy state was set to approval */
    ActionStatus["APPROVAL_PENDING"] = "approval_pending";
    /** When the actual was manually skipped by the user */
    ActionStatus["SKIPPED"] = "skipped";
})(ActionStatus || (ActionStatus = {}));
export var ActionSubStatus;
(function (ActionSubStatus) {
    /** Set while a pending action's processing has been pushed back via deferred_until (API load budget or retry-after-error) */
    ActionSubStatus["DEFERRED"] = "deferred";
    /** When a statement interest charge ran out, but did not generate interest */
    ActionSubStatus["NO_INTEREST_GENERATED"] = "no_interest_generated";
    /** When a auto statement check happened okay, but did not send a statement due to other policy conditions */
    ActionSubStatus["NO_STATEMENT_SENT"] = "no_statement_sent";
})(ActionSubStatus || (ActionSubStatus = {}));
/** Sentinel value for the actions table substatus filter meaning "no substatus set" */
export const ACTION_SUBSTATUS_NONE = `none`;
/** Generic (action type agnostic) labels for the substatus filter options */
export const actionSubStatusFilterLabels = {
    [ACTION_SUBSTATUS_NONE]: `None`,
    [ActionSubStatus.DEFERRED]: `Deferred`,
    [ActionSubStatus.NO_INTEREST_GENERATED]: `No Interest Issued`,
    [ActionSubStatus.NO_STATEMENT_SENT]: `No Statement Sent`,
};
/** When we want to treat reverted like completed, e.g calculating repeating */
export const completeLikeActionStatuses = [
    ActionStatus.COMPLETE,
    ActionStatus.REVERTED,
];
export var ActionType;
(function (ActionType) {
    ActionType["LATE_FEE"] = "late_fee";
    ActionType["LATE_FEE_EMAIL"] = "late_fee_email";
    ActionType["REMINDER"] = "reminder";
    ActionType["DISCOUNT_APPLY"] = "discount_apply";
    ActionType["DISCOUNT_REMOVE"] = "discount_remove";
    ActionType["DISCOUNT_APPLIED_EMAIL"] = "discount_applied_email";
    ActionType["DISCOUNT_REMOVED_EMAIL"] = "discount_removed_email";
    ActionType["SEND_SMS"] = "send_sms";
    /** A job to check for any contact based late fees */
    ActionType["ISSUE_STATEMENT_LATE_FEE"] = "issue_statement_late_fee";
    /** Per Contact */
    ActionType["SEND_STATEMENT"] = "send_statement";
    ActionType["ESCALATION"] = "escalation";
    ActionType["SEND_STATEMENT_INTEREST_EMAIL"] = "send_statement_interest_email";
    ActionType["SEND_ANNOUNCEMENT"] = "send_announcement_email";
    /** Per Quote */
    ActionType["QUOTE_EMAIL"] = "quote_email";
    /** Payment Plan Reminders */
    ActionType["PAYMENT_PLAN_REMINDER"] = "payment_plan_reminder";
    /** Generic on-demand/manual action placeholder */
    ActionType["ON_DEMAND"] = "on_demand";
})(ActionType || (ActionType = {}));
export var ActionOnDemandType;
(function (ActionOnDemandType) {
    ActionOnDemandType["SMS"] = "sms";
    ActionOnDemandType["EMAIL"] = "email";
    ActionOnDemandType["STATEMENT"] = "statement";
})(ActionOnDemandType || (ActionOnDemandType = {}));
export const emailTypes = [
    ActionType.DISCOUNT_APPLIED_EMAIL,
    ActionType.DISCOUNT_REMOVED_EMAIL,
    ActionType.LATE_FEE_EMAIL,
    ActionType.REMINDER,
    ActionType.SEND_STATEMENT,
    ActionType.SEND_STATEMENT_INTEREST_EMAIL,
    ActionType.SEND_ANNOUNCEMENT,
    ActionType.QUOTE_EMAIL,
    ActionType.PAYMENT_PLAN_REMINDER,
];
/** Keeping this as a known list to keep it easy to find notes later */
export var ActionNotes;
(function (ActionNotes) {
    /** When a new invoice fallback was applied - locked */
    ActionNotes["NEW_LATE_FEE_INVOICE_DUE_TO_LOCKED"] = "NEW_LATE_FEE_INVOICE_DUE_TO_LOCKED";
    /** When a new invoice fallback was applied - part paid */
    ActionNotes["NEW_LATE_FEE_INVOICE_DUE_TO_PART_PAID"] = "NEW_LATE_FEE_INVOICE_DUE_TO_PART_PAID";
    /** When no outstanding balance on statement send */
    ActionNotes["NO_OUTSTANDING_BALANCE_SEND_STATEMENT"] = "NO_OUTSTANDING_BALANCE_SEND_STATEMENT";
    /** When no overdue balance, and policy set to overdue */
    ActionNotes["NO_OVERDUE_BALANCE_SEND_STATEMENT"] = "NO_OVERDUE_BALANCE_SEND_STATEMENT";
    /** Min balance not met */
    ActionNotes["MIN_DUE_BALANCE_NOT_MET_SEND_STATEMENT"] = "MIN_DUE_BALANCE_NOT_MET_SEND_STATEMENT";
    ActionNotes["NO_NEGATIVE_BALANCE_SEND_STATEMENT"] = "NO_NEGATIVE_BALANCE_SEND_STATEMENT";
    /** MIN age not met */
    ActionNotes["NO_OVERDUE_AFTER_MIN_AGE"] = "NO_OVERDUE_AFTER_MIN_AGE";
    /** MAX age not met */
    ActionNotes["NO_OVERDUE_BEFORE_MAX_AGE"] = "NO_OVERDUE_BEFORE_MAX_AGE";
    /** Generic catch when policy checks not met */
    ActionNotes["NO_GENERAL_BALANCES"] = "NO_GENERAL_BALANCES";
    /** We failed to mark as sent in accounting system */
    ActionNotes["FAILED_TO_MARK_SENT_EXTERNAL"] = "FAILED_TO_MARK_SENT_EXTERNAL";
    /** WHEN CHILD ACTIONS ARE MANUALLY RECREATED */
    ActionNotes["CHILD_ACTIONS_RECREATED"] = "CHILD_ACTIONS_RECREATED";
})(ActionNotes || (ActionNotes = {}));
export const actionNotesReadable = {
    [ActionNotes.NEW_LATE_FEE_INVOICE_DUE_TO_LOCKED]: `Late fee was issued as a new invoice due to the current invoice being in a locked period`,
    [ActionNotes.NEW_LATE_FEE_INVOICE_DUE_TO_PART_PAID]: `Late fee was issued as a new invoice due to payments or credit notes on the current invoice`,
    [ActionNotes.NO_OUTSTANDING_BALANCE_SEND_STATEMENT]: `No outstanding balance, so no statement was sent`,
    [ActionNotes.NO_OVERDUE_BALANCE_SEND_STATEMENT]: `No overdue balance for contact, so no statement was sent`,
    [ActionNotes.NO_OVERDUE_AFTER_MIN_AGE]: `No overdue balance for contact after applying a min debt age, so no statement was sent`,
    [ActionNotes.NO_OVERDUE_BEFORE_MAX_AGE]: `No overdue balance for contact after applying a max debt age, so no statement was sent`,
    [ActionNotes.MIN_DUE_BALANCE_NOT_MET_SEND_STATEMENT]: `Minimum due balance not met, so no statement was sent`,
    [ActionNotes.FAILED_TO_MARK_SENT_EXTERNAL]: `Email was sent successfully, but failed to mark as sent in accounting system`,
    [ActionNotes.CHILD_ACTIONS_RECREATED]: `This action was recreated manually`,
    [ActionNotes.NO_NEGATIVE_BALANCE_SEND_STATEMENT]: `No negative balance statements found, so no statement was sent`,
    [ActionNotes.NO_GENERAL_BALANCES]: `Policy balance conditions were not met, so no statement was sent`,
};
export const policyActionMap = new Map([
    [ActionType.LATE_FEE, PolicyType.LATE_FEE],
    [ActionType.REMINDER, PolicyType.REMINDER],
    [ActionType.SEND_SMS, PolicyType.SMS],
    [ActionType.ON_DEMAND, PolicyType.ON_DEMAND_PLACEHOLDER],
]);
export const actionSortMap = new Map([
    [`invoiceNumber`, `invoice_number`],
    [`type`, `type`],
    [`actionOn`, `action_on`],
    [`executedOn`, `executed_date`],
    [`status`, `status`],
    [`updatedAt`, `updated_at`],
]);
/** What the action is applied to */
export var ActionTargetResource;
(function (ActionTargetResource) {
    /** E.g Late fees per invoice */
    ActionTargetResource["INVOICE"] = "INVOICE";
    /** E.g Org Disconnect, Or statement check */
    ActionTargetResource["ORGANISATION"] = "ORGANISATION";
    /** Statements */
    ActionTargetResource["CONTACT"] = "CONTACT";
    ActionTargetResource["QUOTE"] = "QUOTE";
    ActionTargetResource["PAYMENT_PLAN_INSTALMENT"] = "PAYMENT_PLAN_INSTALMENT";
})(ActionTargetResource || (ActionTargetResource = {}));
/** All Require valid subscription be linked to be ran, except for ALWAYS */
export var PendingActionGroup;
(function (PendingActionGroup) {
    PendingActionGroup["NONE"] = "NONE";
    PendingActionGroup["LATE_FEE"] = "LATE_FEE";
    PendingActionGroup["LATE_FEE_EMAIL"] = "LATE_FEE_EMAIL";
    PendingActionGroup["NORMAL"] = "NORMAL";
})(PendingActionGroup || (PendingActionGroup = {}));
export const actionConfig = {
    [ActionType.REMINDER]: {
        originPolicyType: PolicyType.REMINDER,
        targetResource: ActionTargetResource.INVOICE,
        includedInActionGeneration: true,
        billableType: `email`,
        canActionNow: true,
        canRetryOnFail: true,
        pendingKickOffGroup: PendingActionGroup.NORMAL,
        color: `#0269A1`,
        allowedSubStatuses: [ActionSubStatus.DEFERRED],
        canBeSkipped: true,
        firesWebhook: true,
        apiLoad: {
            [`XERO`]: 2, // invoice PDF on cache miss; optional attachments fetch / mark-as-sent
            [`QBO`]: 2, // invoice PDF on cache miss; optional attachments fetch / mark-as-sent
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
    [ActionType.SEND_SMS]: {
        originPolicyType: PolicyType.SMS,
        targetResource: ActionTargetResource.INVOICE,
        includedInActionGeneration: true,
        canRemoveOnFail: true,
        canRetryOnFail: true,
        canActionNow: true,
        billableType: `action`,
        pendingKickOffGroup: PendingActionGroup.NORMAL,
        color: `#0F766E`,
        allowedSubStatuses: [ActionSubStatus.DEFERRED],
        canBeSkipped: true,
        firesWebhook: true,
        apiLoad: {
            [`XERO`]: 1, // latest-invoice validation check (checkHaveLatestChangeForContact) when building SMS template statement; skipValidation only on previews
            [`QBO`]: 1, // latest-invoice validation check (checkHaveLatestChangeForContact) when building SMS template statement; skipValidation only on previews
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
    [ActionType.LATE_FEE]: {
        originPolicyType: PolicyType.LATE_FEE,
        targetResource: ActionTargetResource.INVOICE,
        includedInActionGeneration: true,
        canActionNow: true,
        canRemoveOnFail: true,
        canRetryOnFail: true,
        billableType: `action`,
        pendingKickOffGroup: PendingActionGroup.LATE_FEE,
        color: `#C2410D`,
        allowedSubStatuses: [ActionSubStatus.DEFERRED],
        canBeSkipped: true,
        firesWebhook: true,
        apiLoad: {
            [`XERO`]: 4, // refresh invoice, invoice-history/created-by-us check, then add fee line items or create penalty invoice + resync
            [`QBO`]: 3, // refresh invoice, then add fee line items or create penalty invoice + resync
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
    [ActionType.LATE_FEE_EMAIL]: {
        targetResource: ActionTargetResource.INVOICE,
        includedInActionGeneration: false,
        billableType: `email`,
        canActionNow: true,
        canRemovePending: true,
        pendingKickOffGroup: PendingActionGroup.LATE_FEE_EMAIL,
        allowedSubStatuses: [ActionSubStatus.DEFERRED],
        firesWebhook: true,
        apiLoad: {
            [`XERO`]: 3, // invoice PDF on cache miss + penalty invoice PDF + get invoice for number; optional mark-as-sent
            [`QBO`]: 3, // invoice PDF on cache miss + penalty invoice PDF + get invoice for number; optional mark-as-sent
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
    [ActionType.DISCOUNT_APPLIED_EMAIL]: {
        targetResource: ActionTargetResource.INVOICE,
        includedInActionGeneration: false,
        canActionNow: true,
        billableType: `email`,
        pendingKickOffGroup: PendingActionGroup.NORMAL,
        allowedSubStatuses: [ActionSubStatus.DEFERRED],
        firesWebhook: true,
        apiLoad: {
            [`XERO`]: 3, // invoice PDF on cache miss + discounted invoice PDF + get invoice; optional mark-as-sent
            [`QBO`]: 3, // invoice PDF on cache miss + discounted invoice PDF + get invoice; optional mark-as-sent
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
    [ActionType.DISCOUNT_REMOVE]: {
        targetResource: ActionTargetResource.INVOICE,
        includedInActionGeneration: false,
        billableType: `action`,
        pendingKickOffGroup: PendingActionGroup.NORMAL,
        allowedSubStatuses: [ActionSubStatus.DEFERRED],
        firesWebhook: true,
        apiLoad: {
            [`XERO`]: 2, // get invoice + update to remove discount line items
            [`QBO`]: 2, // find invoice + sparse update to remove discount line items
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
    [ActionType.DISCOUNT_APPLY]: {
        originPolicyType: PolicyType.DISCOUNT,
        targetResource: ActionTargetResource.INVOICE,
        includedInActionGeneration: true,
        pendingKickOffGroup: PendingActionGroup.NORMAL,
        color: `#7E23CE`,
        canActionNow: true,
        allowedSubStatuses: [ActionSubStatus.DEFERRED],
        firesWebhook: true,
        apiLoad: {
            [`XERO`]: 3, // get + update invoice line items; credit-note path can add create credit note + allocation
            [`QBO`]: 2, // get + update invoice line items
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
    [ActionType.DISCOUNT_REMOVED_EMAIL]: {
        targetResource: ActionTargetResource.INVOICE,
        includedInActionGeneration: false,
        billableType: `email`,
        pendingKickOffGroup: PendingActionGroup.NORMAL,
        allowedSubStatuses: [ActionSubStatus.DEFERRED],
        firesWebhook: true,
        apiLoad: {
            [`XERO`]: 1, // invoice PDF on cache miss
            [`QBO`]: 1, // invoice PDF on cache miss
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
    [ActionType.ISSUE_STATEMENT_LATE_FEE]: {
        targetResource: ActionTargetResource.CONTACT,
        includedInActionGeneration: true,
        canActionNow: true,
        canRemoveOnFail: true,
        canRetryOnFail: true,
        billableType: `action`,
        pendingKickOffGroup: PendingActionGroup.LATE_FEE,
        color: `#f7a809`,
        allowedSubStatuses: [ActionSubStatus.DEFERRED, ActionSubStatus.NO_INTEREST_GENERATED],
        canBeSkipped: true,
        firesWebhook: true,
        apiLoad: {
            [`XERO`]: 10, // full historical contact resync (invoices, credit notes, payments) then per-currency invoice create + resync
            [`QBO`]: 10, // full historical contact resync (invoices, credit notes, payments) then per-currency invoice create + resync
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
    [ActionType.SEND_STATEMENT]: {
        targetResource: ActionTargetResource.CONTACT,
        includedInActionGeneration: true,
        canActionNow: true,
        canRemoveOnFail: true,
        canRetryOnFail: true,
        billableType: `email`,
        pendingKickOffGroup: PendingActionGroup.NORMAL,
        color: `#6990f4`,
        allowedSubStatuses: [ActionSubStatus.DEFERRED, ActionSubStatus.NO_STATEMENT_SENT],
        canBeSkipped: true,
        firesWebhook: true,
        apiLoad: {
            [`XERO`]: 1, // latest-invoice validation check (checkHaveLatestChangeForContact) when building statement (createStatement without skipValidation)
            [`QBO`]: 1, // latest-invoice validation check (checkHaveLatestChangeForContact) when building statement (createStatement without skipValidation)
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
    [ActionType.ESCALATION]: {
        originPolicyType: PolicyType.ESCALATION,
        targetResource: ActionTargetResource.INVOICE,
        includedInActionGeneration: true,
        canActionNow: true,
        canRemoveOnFail: true,
        canRetryOnFail: true,
        billableType: `action`,
        pendingKickOffGroup: PendingActionGroup.NORMAL,
        color: `#f78c09`,
        allowedSubStatuses: [ActionSubStatus.DEFERRED],
        canBeSkipped: true,
        firesWebhook: true,
        apiLoad: {
            [`XERO`]: 0, // no accounting-provider calls at process time
            [`QBO`]: 0, // no accounting-provider calls at process time
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
    [ActionType.SEND_STATEMENT_INTEREST_EMAIL]: {
        targetResource: ActionTargetResource.CONTACT,
        includedInActionGeneration: false,
        billableType: `email`,
        canActionNow: true,
        canRetryOnFail: true,
        pendingKickOffGroup: PendingActionGroup.LATE_FEE_EMAIL,
        allowedSubStatuses: [ActionSubStatus.DEFERRED],
        firesWebhook: true,
        apiLoad: {
            [`XERO`]: 1, // interest invoice PDF on cache miss
            [`QBO`]: 1, // interest invoice PDF on cache miss
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
    [ActionType.SEND_ANNOUNCEMENT]: {
        targetResource: ActionTargetResource.CONTACT,
        includedInActionGeneration: false,
        billableType: `email`,
        canActionNow: true,
        pendingKickOffGroup: PendingActionGroup.NORMAL,
        allowedSubStatuses: [ActionSubStatus.DEFERRED],
        firesWebhook: true,
        apiLoad: {
            [`XERO`]: 0, // no accounting-provider calls at process time
            [`QBO`]: 0, // no accounting-provider calls at process time
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
    [ActionType.QUOTE_EMAIL]: {
        originPolicyType: PolicyType.QUOTE_EMAIL,
        targetResource: ActionTargetResource.QUOTE,
        includedInActionGeneration: true,
        billableType: `email`,
        pendingKickOffGroup: PendingActionGroup.NORMAL,
        canActionNow: true,
        canRemoveOnFail: true,
        canRetryOnFail: true,
        allowedSubStatuses: [ActionSubStatus.DEFERRED],
        firesWebhook: true,
        apiLoad: {
            [`XERO`]: 0, // no accounting-provider calls at process time
            [`QBO`]: 0, // no accounting-provider calls at process time
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
    [ActionType.PAYMENT_PLAN_REMINDER]: {
        originPolicyType: PolicyType.PAYMENT_PLAN_REMINDER,
        targetResource: ActionTargetResource.PAYMENT_PLAN_INSTALMENT,
        includedInActionGeneration: true,
        billableType: `email`,
        pendingKickOffGroup: PendingActionGroup.NORMAL,
        canActionNow: true,
        canRemoveOnFail: true,
        canRetryOnFail: true,
        allowedSubStatuses: [ActionSubStatus.DEFERRED],
        firesWebhook: true,
        apiLoad: {
            [`XERO`]: 1, // invoice PDF only when policy attaches it
            [`QBO`]: 1, // invoice PDF only when policy attaches it
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
    [ActionType.ON_DEMAND]: {
        originPolicyType: PolicyType.ON_DEMAND_PLACEHOLDER,
        targetResource: ActionTargetResource.CONTACT,
        includedInActionGeneration: false,
        canActionNow: true,
        canRemoveOnFail: true,
        canRetryOnFail: true,
        billableType: `action`,
        pendingKickOffGroup: PendingActionGroup.NORMAL,
        allowedSubStatuses: [ActionSubStatus.DEFERRED],
        firesWebhook: false,
        apiLoad: {
            [`XERO`]: 1, // invoice PDF only when the email attaches it
            [`QBO`]: 1, // invoice PDF only when the email attaches it
            [`INTERNAL`]: 0, // no external accounting system
        },
    },
};
export function getApiLoadEstimate(actionType, provider) {
    return actionConfig[actionType].apiLoad[provider];
}
export function findActionTypeFromPolicy(type) {
    for (const [key, value] of policyActionMap) {
        if (value === type) {
            return key;
        }
    }
    return null;
}
export const actionStatementTypes = [
    ActionType.ISSUE_STATEMENT_LATE_FEE,
    ActionType.SEND_STATEMENT,
];
export const generateActionTypes = Object.keys(actionConfig).filter(key => actionConfig[key].includedInActionGeneration);
//# sourceMappingURL=action.js.map